/**
 * Phone OTP delivery recovery — enterprise escape hatch when SMS cannot complete.
 *
 * Never a dead end: primary path is always a real alternative (email / app / retry),
 * never "wait forever for a code that will not arrive".
 *
 * Design bar: calm (not alarm-red), scannable cause, one obvious next step,
 * secondary exits always visible, countdown baked into the retry label,
 * font-scale safe, reduced-motion safe.
 */
import { useMemo } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { AlertTriangle, Clock, MessageCircle, Phone } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { AuthGradientButton } from '@/components/auth/AuthGradientButton'
import type { PhoneOtpDeliveryReason } from '@/lib/errors/service-error'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useSemanticColors, withAlpha, DURATION, STAGGER, INTERACTION } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useFontScale } from '@/lib/hooks/use-font-scale'

export type PhoneOtpEmailCta =
  | 'sign_in_email'
  | 'create_account_email'
  | 'continue_app'
  | 'verify_email'
  | null

export type PhoneOtpDeliveryRecoveryProps = {
  reason: PhoneOtpDeliveryReason
  mode?: 'send' | 'resend'
  /** Primary alternate path — email login, register, or enter app. */
  emailCta: PhoneOtpEmailCta
  onEmailCta: () => void
  /** Optional clear second exit: Google on the signup/login screens. */
  onGoogleCta?: () => void
  googleLoading?: boolean
  onRetry?: () => void
  retryLoading?: boolean
  retryDisabled?: boolean
  retrySeconds?: number
  onChangeNumber?: () => void
  onSupport?: () => void
  testID?: string
}

type RecoveryTone = 'wait' | 'provider' | 'generic'

/*
 * Se fue un tono. `fraud` existía para el bloqueo 60410 del proveedor anterior
 * —"el prefijo está bloqueado, esperar no lo arregla"— y ningún código del
 * proveedor actual significa eso, así que nada podía encenderlo. Un tono que no
 * se puede alcanzar no es una opción de más: es una rama que se lee como
 * cubierta y no lo está.
 */
function resolveTone(reason: PhoneOtpDeliveryReason): RecoveryTone {
  if (reason === 'provider_rejected') return 'provider'
  if (reason === 'service_rate_limited' || reason === 'account_rate_limited') return 'wait'
  return 'generic'
}

export function PhoneOtpDeliveryRecovery({
  reason,
  mode = 'send',
  emailCta,
  onEmailCta,
  onGoogleCta,
  googleLoading = false,
  onRetry,
  retryLoading = false,
  retryDisabled = false,
  retrySeconds = 0,
  onChangeNumber,
  onSupport,
  testID = 'phone-otp-delivery-recovery',
}: PhoneOtpDeliveryRecoveryProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const { box, enlarged } = useFontScale()

  const tone = resolveTone(reason)

  const copy = useMemo(() => {
    switch (reason) {
      case 'service_rate_limited':
        return {
          title: t('auth.verify_phone_sms_busy_title'),
          body: t('auth.verify_phone_sms_busy_body'),
        }
      case 'account_rate_limited':
        return {
          title: t('auth.verify_phone_sms_quota_title'),
          body: t('auth.verify_phone_sms_quota_body'),
        }
      case 'provider_rejected':
        return {
          title: t('auth.verify_phone_sms_provider_title'),
          body: t('auth.verify_phone_sms_provider_body'),
        }
      default:
        return {
          title: t('auth.verify_phone_sms_unavailable_title'),
          body: t(
            mode === 'resend'
              ? 'auth.verify_phone_sms_unavailable_resend_body'
              : 'auth.verify_phone_sms_unavailable_send_body',
          ),
        }
    }
  }, [mode, reason, t])

  const emailLabel = useMemo(() => {
    switch (emailCta) {
      case 'sign_in_email':
      case 'create_account_email':
        // One clear label for both login and signup escapes — destination
        // still differs (login vs register) via the parent handler.
        return t('auth.otp_recovery_continue_email')
      case 'continue_app':
        return t('auth.otp_recovery_continue_app')
      case 'verify_email':
        return t('auth.otp_recovery_verify_email')
      default:
        return null
    }
  }, [emailCta, t])

  const googleLabel = onGoogleCta ? t('auth.otp_recovery_continue_google') : null

  // Caída del proveedor: esperar rara vez ayuda, así que la salida por correo /
  // Google va primero. Límites de tasa: reintentar primero, porque ahí esperar
  // SÍ es la respuesta.
  const hasEmail = Boolean(emailCta && emailLabel)
  const leadWithEmail = hasEmail && (reason === 'provider_rejected' || !onRetry)
  const retryBlocked = Boolean(retryDisabled || retrySeconds > 0)
  const retryLabel =
    retrySeconds > 0
      ? t('auth.verify_phone_sms_unavailable_retry_in', { seconds: retrySeconds })
      : t('auth.verify_phone_sms_unavailable_retry')

  const iconMeta = useMemo(() => {
    const coral = COLORS.coral.DEFAULT
    switch (tone) {
      case 'provider':
        return {
          Icon: AlertTriangle,
          ink: coral,
          disc: withAlpha(coral, isDark ? 0.18 : 0.12),
        }
      case 'wait':
        return {
          Icon: Clock,
          ink: isDark ? colors.primaryLight : colors.primary,
          disc: withAlpha(isDark ? colors.primaryLight : colors.primary, isDark ? 0.18 : 0.1),
        }
      default:
        return {
          Icon: MessageCircle,
          ink: coral,
          disc: withAlpha(coral, isDark ? 0.18 : 0.12),
        }
    }
  }, [tone, isDark, colors.primary, colors.primaryLight])

  const surface = withAlpha(COLORS.coral.DEFAULT, isDark ? 0.09 : 0.055)
  const border = withAlpha(COLORS.coral.DEFAULT, isDark ? 0.3 : 0.18)
  const accentBar = withAlpha(COLORS.coral.DEFAULT, isDark ? 0.55 : 0.45)
  const discSize = box(40)
  const Icon = iconMeta.Icon
  const liveRegion = tone === 'provider' ? 'assertive' : 'polite'

  const handleRetry = () => {
    if (retryBlocked || retryLoading) return
    onRetry?.()
  }

  return (
    <Animated.View
      collapsable={false}
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER)}
      style={{
        marginTop: SPACING.md,
        borderRadius: RADIUS.lg,
        backgroundColor: surface,
        borderWidth: 1,
        borderColor: border,
        overflow: 'hidden',
      }}
      testID={testID}
      accessibilityRole="alert"
      accessibilityLabel={`${copy.title}. ${copy.body}`}
    >
      {/* Left accent — brands the card without screaming “error red”. */}
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          backgroundColor: accentBar,
        }}
      />

      <View style={{ padding: SPACING.lg, paddingLeft: SPACING.lg + 2, gap: SPACING.md }}>
        {/* ── Header: icon + copy ── */}
        <View
          style={{
            flexDirection: enlarged ? 'column' : 'row',
            alignItems: enlarged ? 'flex-start' : 'flex-start',
            gap: SPACING.md,
          }}
          accessibilityLiveRegion={liveRegion}
        >
          <View
            accessible={false}
            style={{
              width: discSize,
              height: discSize,
              borderRadius: discSize / 2,
              backgroundColor: iconMeta.disc,
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={enlarged ? 20 : 18} color={iconMeta.ink} strokeWidth={2.25} />
          </View>

          <View style={{ flex: 1, minWidth: 0, gap: SPACING.xs }}>
            <Text
              accessibilityRole="header"
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 17,
                lineHeight: 22,
                color: colors.textPrimary,
                letterSpacing: -0.2,
              }}
            >
              {copy.title}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 15,
                lineHeight: 22,
                color: colors.textSecondary,
              }}
            >
              {copy.body}
            </Text>
          </View>
        </View>

        {/* ── Actions ── */}
        <View style={{ gap: SPACING.sm }}>
          {leadWithEmail && emailLabel ? (
            <AuthGradientButton
              title={emailLabel}
              onPress={onEmailCta}
              testID={`${testID}.email-cta`}
            />
          ) : null}

          {leadWithEmail && googleLabel ? (
            <AuthGradientButton
              title={googleLabel}
              onPress={onGoogleCta!}
              loading={googleLoading}
              variant="secondary"
              testID={`${testID}.google-cta`}
            />
          ) : null}

          {onRetry && !leadWithEmail ? (
            <AuthGradientButton
              title={retryLabel}
              onPress={handleRetry}
              loading={retryLoading}
              disabled={retryBlocked}
              testID={`${testID}.retry`}
            />
          ) : null}

          {onRetry && leadWithEmail ? (
            <Button
              title={retryLabel}
              onPress={handleRetry}
              loading={retryLoading}
              disabled={retryBlocked}
              variant="secondary"
              testID={`${testID}.retry`}
            />
          ) : null}

          {!leadWithEmail && emailLabel ? (
            <AuthGradientButton
              title={emailLabel}
              onPress={onEmailCta}
              variant="secondary"
              testID={`${testID}.email-cta`}
            />
          ) : null}

          {!leadWithEmail && googleLabel ? (
            <AuthGradientButton
              title={googleLabel}
              onPress={onGoogleCta!}
              loading={googleLoading}
              variant="secondary"
              testID={`${testID}.google-cta`}
            />
          ) : null}

          {/* Soft path hint when email/Google is the escape. */}
          {leadWithEmail && emailLabel ? (
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 18,
                color: colors.textMuted,
                textAlign: 'center',
                marginTop: SPACING.xs / 2,
              }}
              testID={`${testID}.hint`}
            >
              {t('auth.otp_recovery_hint')}
            </Text>
          ) : null}
        </View>

        {/* ── Secondary exits ── */}
        {onChangeNumber || onSupport ? (
          <View
            style={{
              borderTopWidth: 1,
              borderTopColor: withAlpha(COLORS.coral.DEFAULT, isDark ? 0.16 : 0.1),
              paddingTop: SPACING.md,
              marginTop: SPACING.xs,
              flexDirection: enlarged ? 'column' : 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: enlarged ? 'stretch' : 'center',
              gap: SPACING.sm,
            }}
          >
            {onChangeNumber ? (
              <View
                style={{
                  flexGrow: 1,
                  flexBasis: enlarged ? '100%' : '40%',
                  minHeight: INTERACTION.minTouchTarget,
                  justifyContent: 'center',
                }}
              >
                <Button
                  title={t('auth.verify_phone_sms_unavailable_change_number')}
                  variant="text"
                  onPress={onChangeNumber}
                  icon={<Phone size={15} color={COLORS.coral.DEFAULT} strokeWidth={2} />}
                  testID={`${testID}.change-number`}
                  accessibilityLabel={t('auth.verify_phone_sms_unavailable_change_number')}
                />
              </View>
            ) : null}
            {onSupport ? (
              <View
                style={{
                  flexGrow: 1,
                  flexBasis: enlarged ? '100%' : '40%',
                  minHeight: INTERACTION.minTouchTarget,
                  justifyContent: 'center',
                }}
              >
                <Button
                  title={t('auth.verify_phone_sms_unavailable_support_short')}
                  accessibilityLabel={t('auth.verify_phone_sms_unavailable_support')}
                  variant="text"
                  onPress={onSupport}
                  icon={<MessageCircle size={15} color={COLORS.coral.DEFAULT} strokeWidth={2} />}
                  testID={`${testID}.support`}
                />
              </View>
            ) : null}
          </View>
        ) : null}
      </View>
    </Animated.View>
  )
}
